import { useState, useLayoutEffect, useEffect, useCallback } from 'react'
import useFrameCoalescedCallback from './useFrameCoalescedCallback'
import useVisibleSlice from './useVisibleSlice'
import { frameOffset } from '../../shared/scroll'
import { hostWindow } from '../../shared/host_window'
import { getVisibleSlice } from '../utils/visible_slice'

// Floors, so a pinned toast never touches the viewport edge.
const GAP_Y = 12
const GAP_X = 16

// The top the toast must not rise above: the screen's top edge, which on a
// foreign host is the visible slice's (utils/visible_slice), not the frame's.
const topFloor = () => {
  const slice = getVisibleSlice()
  return GAP_Y + (slice ? slice.top : 0)
}

const DEFAULT_POSITION = { top: GAP_Y, right: GAP_X }

// The share / rotate cluster the toast covers. Web renders one in the top bar,
// mobile and landscape one per open tile, so take the first that is laid out.
const findActions = () =>
  Array.from(document.querySelectorAll('.lbv2__header-actions'))
    .find((el) => el.getBoundingClientRect().height > 0)

// Cross-document sticky. The leaderboard usually lives in a same-origin iframe
// that widgets.init_iframe keeps at full content height, so the frame itself
// never scrolls and `position: sticky` inside it can never trigger — the parent
// page owns the scroll. Measuring in the top viewport reproduces sticky from a
// fixed-position toast.
//
// The toast sits over the action buttons, as in Figma: bottoms aligned, right
// edges aligned. Without the cluster (manager site) it falls back to the top
// right of the leaderboard itself.
const measurePosition = (toastHeight) => {
  const anchor = findActions() || document.getElementById('lbv2__leaderboard')
  if (!anchor) {
    return DEFAULT_POSITION
  }

  const frame = frameOffset()
  const rect = anchor.getBoundingClientRect()
  const viewportWidth = hostWindow().innerWidth
  const anchorTop = anchor.id === 'lbv2__leaderboard'
    ? rect.top + GAP_Y
    : rect.bottom - toastHeight

  return {
    top: Math.max(topFloor(), frame.top + anchorTop),
    right: Math.max(GAP_X, viewportWidth - (frame.left + rect.right)),
  }
}

// Returns the toast's { top, right } in pixels, recomputed while active is true.
// toastRef supplies the rendered height the bottom alignment needs.
const useStickyToastPosition = (active, toastRef) => {
  const [ position, setPosition ] = useState(DEFAULT_POSITION)

  const update = useCallback(() => {
    const height = toastRef.current ? toastRef.current.offsetHeight : 0
    const next = measurePosition(height)
    // Keep the previous state object when nothing moved, so React bails out of
    // re-rendering the toast - most scroll frames leave a pinned toast where
    // it already is.
    setPosition((prev) =>
      (prev.top === next.top && prev.right === next.right ? prev : next))
  }, [ toastRef ])

  // Scroll and resize can fire on two windows in the same frame - our own and
  // the embedding host's.
  const scheduleUpdate = useFrameCoalescedCallback(update)

  // Pre-paint, so the first frame already has the measured height instead of
  // flashing at the default offsets. Synchronous on purpose - scheduling this
  // one would paint the default position first.
  useLayoutEffect(() => {
    if (active) {
      update()
    }
  }, [ active, update ])

  useEffect(() => {
    if (!active) {
      return undefined
    }

    const targets = [ window ]
    const scrollWindow = hostWindow()
    if (scrollWindow !== window) {
      targets.push(scrollWindow)
    }

    targets.forEach((target) => {
      target.addEventListener('scroll', scheduleUpdate, { passive: true })
      target.addEventListener('resize', scheduleUpdate)
    })

    return () => {
      targets.forEach((target) => {
        target.removeEventListener('scroll', scheduleUpdate)
        target.removeEventListener('resize', scheduleUpdate)
      })
    }
  }, [ active, scheduleUpdate ])

  // A foreign host's scroll reaches none of the listeners above.
  useVisibleSlice(active, scheduleUpdate)

  return position
}

export default useStickyToastPosition
